import { RemoteImagesContext } from "@rakazo/chat-ui/web";
import type { ShoppingEntry as EntrySchema } from "@rakazo/contracts";
import type { z } from "zod";
import { Button } from "@rakazo/ui-web";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Image as ImageIcon, Package, ShoppingBag } from "lucide-react";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { useShoppingScope } from "../lib/shopping-scope";

export type ShoppingEntryType = z.infer<typeof EntrySchema>;
export function shoppingMoney(money?: { amount: number; currency: string }) {
  if (!money) return null;
  try {
    const format = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: money.currency,
    });
    return format.format(
      money.amount / 10 ** (format.resolvedOptions().maximumFractionDigits ?? 2),
    );
  } catch {
    return `${money.currency} ${money.amount} minor units`;
  }
}
export const shoppingStatusLabel = (status: string) =>
  ({
    unknown: "Status unknown",
    confirmed: "Confirmed",
    processing: "Processing",
    shipped: "Shipped",
    out_for_delivery: "Out for delivery",
    delivered: "Delivered",
    cancelled: "Cancelled",
    returned: "Returned",
  })[status] ?? status;
export function ProductCard({ entry }: { entry: ShoppingEntryType }) {
  const remote = useContext(RemoteImagesContext);
  const [load, setLoad] = useState(false),
    [failed, setFailed] = useState(false);
  const p = entry.product;
  if (!p) return null;
  return (
    <article
      className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground"
      data-testid="shopping-product-card"
    >
      <div className="flex h-36 items-center justify-center bg-muted/40">
        {p.imageUrl && (remote || load) && !failed ? (
          <img
            src={p.imageUrl}
            alt={p.title}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="size-full object-contain p-3"
            onError={() => setFailed(true)}
          />
        ) : p.imageUrl && !failed ? (
          <Button variant="ghost" size="sm" onClick={() => setLoad(true)}>
            <ImageIcon size={16} />
            Load image
          </Button>
        ) : (
          <ShoppingBag size={28} className="text-muted-foreground" aria-hidden="true" />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="text-xs text-muted-foreground">{p.merchant}</p>
        <h3 className="line-clamp-2 font-medium">{p.title}</h3>
        {p.description ? (
          <p className="line-clamp-2 text-sm text-muted-foreground">{p.description}</p>
        ) : null}
        {p.price ? (
          <p className="text-lg font-semibold">{shoppingMoney(p.price)}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Check price at store</p>
        )}
        <p className="text-xs text-muted-foreground">
          Observed {new Date(p.observedAt).toLocaleDateString()}
        </p>
        <Button
          className="mt-auto"
          size="sm"
          variant="outline"
          nativeButton={false}
          render={<a href={p.url} target="_blank" rel="noopener noreferrer" />}
        >
          View at store
          <ArrowUpRight size={15} />
        </Button>
      </div>
    </article>
  );
}

export function ShoppingLinks({ text }: { text: string }) {
  const ids = [
    ...new Set(
      [...text.matchAll(/\/app\/shopping\?product=([a-z0-9-]{20,90})/g)].map((m) => m[1]!),
    ),
  ].slice(0, 8);
  if (!ids.length) return null;
  return (
    <div className="my-3 grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
      {ids.map((id) => (
        <ShoppingProductLink key={id} id={id} />
      ))}
    </div>
  );
}
function ShoppingProductLink({ id }: { id: string }) {
  const { user, space, rpc } = useShoppingScope();
  const query = useQuery({
    queryKey: ["shopping-product", user, space, id],
    queryFn: () => rpc.shopping.get({ id }),
    retry: false,
    staleTime: 60_000,
  });
  return query.data?.product ? (
    <ProductCard entry={query.data} />
  ) : query.isPending ? (
    <div className="h-56 animate-pulse rounded-2xl bg-muted" aria-label="Loading product" />
  ) : (
    <Link className="text-sm underline" to="/app/shopping">
      <Package size={14} className="inline" /> Open Shopping
    </Link>
  );
}
